import { Badge, Box, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { FiArrowRight } from "react-icons/fi";
import { openapi } from "../../openapi";
import { useAppSelector } from "../app/hooks";
import {
  DrofusForekomstDetailsModal,
  type DrofusForekomstDetailsRef,
} from "../DrofusForekomster/DrofusForekomstDetailsModal";
import { selectHttpSingleRoomFull } from "../Room/httpRoomSelectors";
import { selectDrofusChangesApiArg } from "./drofusChangesSelectors";
import {
  selectRoomDrofusChangeRows,
  selectRoomDrofusChangeSummary,
} from "./roomDrofusChangesSelectors";

const disciplineColorById: Record<string, string> = {
  RIE: "#f59f00",
  RIKT: "#1971c2",
  RIAut: "#9c36b5",
  RIV: "#0c8599",
};

export const RoomDrofusChangesPanel: React.FC = () => {
  const apiArg = useAppSelector(selectDrofusChangesApiArg);
  const { isFetching, isError } = openapi.usePostMultiplacerDrofusInterfaceRoomChangesQuery(
    apiArg ?? skipToken
  );
  const [detailsRef, setDetailsRef] = useState<DrofusForekomstDetailsRef | null>(null);

  const room = useAppSelector(selectHttpSingleRoomFull);
  const rows = useAppSelector(selectRoomDrofusChangeRows);
  const summary = useAppSelector(selectRoomDrofusChangeSummary);

  return (
    <Stack gap="md" p="md" h="100%" style={{ background: "#ffffff", boxSizing: "border-box" }}>
      <DrofusForekomstDetailsModal
        occurrenceRef={detailsRef}
        opened={detailsRef != null}
        onClose={() => setDetailsRef(null)}
      />

      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Box>
          <Title order={4}>Romendringer</Title>
          <Text size="sm" c="dimmed" lineClamp={1}>
            {room?.roomNumber}, {room?.roomName}
          </Text>
        </Box>
        <Badge variant="light" color="blue">
          dRofus
        </Badge>
      </Group>

      <Group grow gap="xs">
        <SummaryBadge label="Endret" value={summary.updated} color="#f59f00" textColor="#1f2937" />
        <SummaryBadge label="Ny" value={summary.added} color="#2f9e44" textColor="#ffffff" />
        <SummaryBadge label="Fjernet" value={summary.removed} color="#e03131" textColor="#ffffff" />
      </Group>

      {isError && (
        <Paper withBorder radius="sm" p="sm" bg="red.0">
          <Text size="sm" c="red.8" fw={600}>
            Kunne ikke hente dRofus-endringer for rommet.
          </Text>
        </Paper>
      )}

      {!isError && rows.length === 0 && (
        <Paper withBorder radius="sm" p="md" bg="gray.0">
          <Text size="sm" c="dimmed">
            {isFetching ? "Laster endringer..." : "Ingen endringer for valgt rom og filter."}
          </Text>
        </Paper>
      )}

      <Stack gap="sm" style={{ minHeight: 0, overflowY: "auto" }}>
        {rows.map((row) => (
          <Paper
            key={row.id}
            withBorder
            radius="sm"
            p="sm"
            component={row.occurrenceRef ? "button" : "div"}
            onClick={row.occurrenceRef ? () => setDetailsRef(row.occurrenceRef) : undefined}
            style={{
              borderLeft: `5px solid ${row.color}`,
              flexShrink: 0,
              cursor: row.occurrenceRef ? "pointer" : "default",
              textAlign: "left",
              width: "100%",
            }}
          >
            <Stack gap={6}>
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Text size="sm" fw={700} lineClamp={1}>
                  {row.title}
                </Text>
                {row.disciplines.length > 0 && (
                  <Group gap={4} wrap="nowrap">
                    {row.disciplines.map((discipline) => (
                      <DisciplineBadge key={discipline} discipline={discipline} />
                    ))}
                  </Group>
                )}
              </Group>

              <Text size="xs" c="dimmed" fw={600}>
                {row.fieldLabel}
              </Text>

              <Group gap="xs" align="center" wrap="nowrap">
                <ValuePill value={row.oldValue} />
                <FiArrowRight size={14} color="#64748b" />
                <ValuePill value={row.newValue} />
              </Group>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
};

const SummaryBadge: React.FC<{
  label: string;
  value: number;
  color: string;
  textColor: string;
}> = ({ label, value, color, textColor }) => (
  <Paper
    radius="sm"
    p="xs"
    style={{
      background: color,
      color: textColor,
      textAlign: "center",
    }}
  >
    <Text size="xs" fw={700}>
      {label}
    </Text>
    <Text size="lg" fw={800} lh={1.1}>
      {value}
    </Text>
  </Paper>
);

const DisciplineBadge: React.FC<{ discipline: string }> = ({ discipline }) => {
  const color = disciplineColorById[discipline] ?? "#868e96";

  return (
    <Badge
      radius="sm"
      styles={{
        root: {
          backgroundColor: "#ffffff",
          border: `1px solid ${color}`,
          color,
        },
        label: { color: "inherit" },
      }}
    >
      {discipline}
    </Badge>
  );
};

const ValuePill: React.FC<{ value: string }> = ({ value }) => (
  <Box
    px="xs"
    py={4}
    style={{
      flex: 1,
      minWidth: 0,
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: 6,
    }}
  >
    <Text size="xs" lineClamp={2}>
      {value}
    </Text>
  </Box>
);
